import React from 'react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { searchDefeitos } from '../../new-os/services/defeitoService';
import {
  getDefeitoTipoBadges,
  hasTipoOsRestriction,
  isTipoAllowedForDefeito,
} from '../../new-os/utils/defeitoTipoRules';
import { getOrderTipoPreventivaCode } from '../utils/orderDetailsModalUtils';

const MOTIVO_OS_MAX = 50;

function filterDefeitosByOrderTipo(items, order) {
  const tipoCode = getOrderTipoPreventivaCode(order);
  const list = Array.isArray(items) ? items : [];
  if (!tipoCode) {
    return list.map((item) => ({
      ...item,
      tipoBadges: getDefeitoTipoBadges(item),
    }));
  }
  return list
    .filter((item) => {
      if (!hasTipoOsRestriction(item)) return true;
      return isTipoAllowedForDefeito(item, tipoCode);
    })
    .map((item) => ({
      ...item,
      tipoBadges: getDefeitoTipoBadges(item),
    }));
}

export function useOrderDetailsDefeitoMotivoEdit(order, { onMotivoUpdated } = {}) {
  const [motivoDisplay, setMotivoDisplay] = React.useState('');
  const [isMotivoEditing, setIsMotivoEditing] = React.useState(false);
  const [motivoEditValue, setMotivoEditValue] = React.useState('');
  const [isSavingMotivo, setIsSavingMotivo] = React.useState(false);
  const [motivoSaveFeedback, setMotivoSaveFeedback] = React.useState(null);

  const [isDefeitoEditing, setIsDefeitoEditing] = React.useState(false);
  const [isObsEditing, setIsObsEditing] = React.useState(false);
  const [defeitoSearchQuery, setDefeitoSearchQuery] = React.useState('');
  const [defeitoSearchResults, setDefeitoSearchResults] = React.useState([]);
  const [isDefeitoSearchLoading, setIsDefeitoSearchLoading] = React.useState(false);
  const [defeitoSearchError, setDefeitoSearchError] = React.useState(null);
  const [editObsValue, setEditObsValue] = React.useState('');
  const [isSavingDefeitoObs, setIsSavingDefeitoObs] = React.useState(false);
  const [defeitoSaveFeedback, setDefeitoSaveFeedback] = React.useState(null);
  const [selectedDefeito, setSelectedDefeito] = React.useState(null);
  const [isDefeitoObsConfirmOpen, setIsDefeitoObsConfirmOpen] = React.useState(false);
  const [isObsGeralEditing, setIsObsGeralEditing] = React.useState(false);
  const [editObsGeralValue, setEditObsGeralValue] = React.useState('');
  const [isSavingObsGeral, setIsSavingObsGeral] = React.useState(false);
  const [obsGeralSaveFeedback, setObsGeralSaveFeedback] = React.useState(null);
  const defeitoSearchTimeoutRef = React.useRef(null);

  React.useEffect(() => {
    const m = String(order?.motivoOS ?? order?.TB02115_NOME ?? '').trim();
    setMotivoDisplay(m);
    setIsMotivoEditing(false);
    setMotivoEditValue('');
    setMotivoSaveFeedback(null);
    setIsDefeitoEditing(false);
    setIsObsEditing(false);
    setIsDefeitoObsConfirmOpen(false);
    setDefeitoSaveFeedback(null);
    setIsObsGeralEditing(false);
    setEditObsGeralValue('');
    setObsGeralSaveFeedback(null);
  }, [order?.id]);

  React.useEffect(
    () => () => {
      if (defeitoSearchTimeoutRef.current) clearTimeout(defeitoSearchTimeoutRef.current);
    },
    []
  );

  const handleStartMotivoEdit = React.useCallback(() => {
    setMotivoEditValue(motivoDisplay);
    setIsMotivoEditing(true);
    setIsObsGeralEditing(false);
    setMotivoSaveFeedback(null);
    setObsGeralSaveFeedback(null);
  }, [motivoDisplay]);

  const handleCancelMotivoEdit = React.useCallback(() => {
    setIsMotivoEditing(false);
    setMotivoEditValue('');
    setMotivoSaveFeedback(null);
  }, []);

  const handleSaveMotivo = React.useCallback(async () => {
    if (!order?.id || isSavingMotivo) return;
    const next = String(motivoEditValue ?? '').trim();
    if (next.length > MOTIVO_OS_MAX) return;
    if (next === motivoDisplay) {
      handleCancelMotivoEdit();
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      setIsSavingMotivo(true);
      setMotivoSaveFeedback(null);
      const res = await fetch(`${getApiBaseUrl()}/api/orders/${order.id}/motivo-os`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo: next }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.message || 'Erro ao salvar motivo');
      setMotivoDisplay(next);
      onMotivoUpdated?.(next);
      setMotivoSaveFeedback({ type: 'success', message: 'Motivo atualizado com sucesso.' });
      setIsMotivoEditing(false);
      setMotivoEditValue('');
      setTimeout(() => setMotivoSaveFeedback(null), 2500);
    } catch (e) {
      setMotivoSaveFeedback({ type: 'error', message: e.message || 'Erro ao salvar motivo.' });
    } finally {
      setIsSavingMotivo(false);
    }
  }, [order?.id, isSavingMotivo, motivoEditValue, motivoDisplay, handleCancelMotivoEdit, onMotivoUpdated]);

  const handleStartDefeitoEdit = React.useCallback((defeitoData) => {
    if (!defeitoData) return;
    setIsDefeitoEditing(true);
    setIsObsEditing(false);
    setIsDefeitoObsConfirmOpen(false);
    setIsObsGeralEditing(false);
    setObsGeralSaveFeedback(null);
    setDefeitoSaveFeedback(null);
    const currentNome = defeitoData.defeito === 'Não informado' ? '' : defeitoData.defeito || '';
    setDefeitoSearchQuery(currentNome);
    if (defeitoData.codDefeito) {
      setSelectedDefeito({
        codigo: defeitoData.codDefeito,
        nome: currentNome,
        solucao: defeitoData.solucao || '',
      });
    } else {
      setSelectedDefeito(null);
    }
  }, []);

  const handleStartObsEdit = React.useCallback((defeitoData) => {
    if (!defeitoData) return;
    setIsObsEditing(true);
    setIsDefeitoEditing(false);
    setIsObsGeralEditing(false);
    setObsGeralSaveFeedback(null);
    setDefeitoSaveFeedback(null);
    const obs = defeitoData.observacao === 'Não informado' ? '' : defeitoData.observacao || '';
    setEditObsValue(obs);
  }, []);

  const handleDefeitoSearchChange = React.useCallback(
    (e) => {
      const value = e.target.value;
      setDefeitoSearchQuery(value);
      setDefeitoSearchError(null);
      if (defeitoSearchTimeoutRef.current) clearTimeout(defeitoSearchTimeoutRef.current);
      if (!value || value.trim().length < 2) {
        setDefeitoSearchResults([]);
        return;
      }
      defeitoSearchTimeoutRef.current = setTimeout(async () => {
        try {
          setIsDefeitoSearchLoading(true);
          const codEquip =
            order?.TB02115_PRODUTO ||
            order?.codprod ||
            order?.TB01010_CODIGO ||
            null;
          const result = await searchDefeitos(value.trim(), codEquip);
          if (result?.success && Array.isArray(result.items)) {
            setDefeitoSearchResults(filterDefeitosByOrderTipo(result.items, order));
          } else {
            setDefeitoSearchResults([]);
            if (result?.error) setDefeitoSearchError(result.error);
          }
        } catch {
          setDefeitoSearchResults([]);
          setDefeitoSearchError('Erro ao buscar defeitos');
        } finally {
          setIsDefeitoSearchLoading(false);
        }
      }, 300);
    },
    [order]
  );

  const handleSelectDefeitoResult = React.useCallback((item) => {
    setSelectedDefeito(item);
    setDefeitoSearchQuery('');
    setDefeitoSearchResults([]);
  }, []);

  const handleClearSelectedDefeito = React.useCallback(() => {
    setSelectedDefeito(null);
    setDefeitoSearchQuery('');
    setDefeitoSearchResults([]);
    setDefeitoSearchError(null);
  }, []);

  const handleCancelDefeitoObsConfirm = React.useCallback(() => {
    setIsDefeitoObsConfirmOpen(false);
  }, []);

  const handleCancelDefeitoObsEdit = React.useCallback((defeitoData) => {
    setIsDefeitoEditing(false);
    setIsObsEditing(false);
    setIsDefeitoObsConfirmOpen(false);
    setDefeitoSearchResults([]);
    setDefeitoSearchQuery('');
    setSelectedDefeito(null);
    if (defeitoData) {
      const obs = defeitoData.observacao === 'Não informado' ? '' : defeitoData.observacao || '';
      setEditObsValue(obs);
    } else {
      setEditObsValue('');
    }
    setDefeitoSaveFeedback(null);
  }, []);

  const handleStartObsGeralEdit = React.useCallback((defeitoData) => {
    if (!defeitoData) return;
    setIsObsGeralEditing(true);
    setIsMotivoEditing(false);
    setIsDefeitoEditing(false);
    setIsObsEditing(false);
    setDefeitoSaveFeedback(null);
    setMotivoSaveFeedback(null);
    setObsGeralSaveFeedback(null);
    setEditObsGeralValue(String(defeitoData.observacaoGeral ?? '').trim());
  }, []);

  const handleCancelObsGeralEdit = React.useCallback((defeitoData) => {
    setIsObsGeralEditing(false);
    setEditObsGeralValue(String(defeitoData?.observacaoGeral ?? '').trim());
    setObsGeralSaveFeedback(null);
  }, []);

  const handleSaveObsGeral = React.useCallback(
    async (defeitoData, setDefeitoData) => {
      if (!defeitoData || isSavingObsGeral || !order?.id) return;
      const current = String(defeitoData.observacaoGeral ?? '').trim();
      const next = String(editObsGeralValue ?? '').trim();
      if (next === current) {
        handleCancelObsGeralEdit(defeitoData);
        return;
      }
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        setIsSavingObsGeral(true);
        setObsGeralSaveFeedback(null);
        const res = await fetch(`${getApiBaseUrl()}/api/orders/${order.id}/observacao-geral`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ observacao: next }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          throw new Error(json.message || 'Erro ao salvar observações');
        }
        setDefeitoData((prev) => ({ ...prev, observacaoGeral: next }));
        setObsGeralSaveFeedback({ type: 'success', message: 'Observações atualizadas com sucesso.' });
        setIsObsGeralEditing(false);
        setTimeout(() => setObsGeralSaveFeedback(null), 2500);
      } catch (error) {
        setObsGeralSaveFeedback({ type: 'error', message: error.message || 'Erro ao salvar observações.' });
      } finally {
        setIsSavingObsGeral(false);
      }
    },
    [order?.id, isSavingObsGeral, editObsGeralValue, handleCancelObsGeralEdit]
  );

  const persistDefeitoObs = React.useCallback(
    async (defeitoData, setDefeitoData, { codigoDefeito, observacao, codDefeitoAnterior }) => {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      setIsSavingDefeitoObs(true);
      setDefeitoSaveFeedback(null);
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/orders/${order.id}/defeito`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            codDefeitoAnterior,
            codigoDefeito,
            observacao,
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          throw new Error(json.message || 'Erro ao salvar alterações de defeito/observação');
        }
        if (json.data) setDefeitoData(json.data);
        setDefeitoSaveFeedback({ type: 'success', message: 'Defeito/observação atualizados com sucesso.' });
        setIsDefeitoEditing(false);
        setIsObsEditing(false);
        setIsDefeitoObsConfirmOpen(false);
        setDefeitoSearchResults([]);
        setSelectedDefeito(null);
        setDefeitoSearchQuery('');
        setTimeout(() => setDefeitoSaveFeedback(null), 2500);
      } catch (error) {
        setDefeitoSaveFeedback({ type: 'error', message: error.message || 'Erro ao salvar alterações.' });
      } finally {
        setIsSavingDefeitoObs(false);
      }
    },
    [order?.id]
  );

  const handleSaveDefeitoObs = React.useCallback(
    async (defeitoData, setDefeitoData) => {
      if (!defeitoData || isSavingDefeitoObs || !order?.id) return;
      const codDefeitoAnterior = defeitoData.codDefeito || null;
      const currentObs = defeitoData.observacao === 'Não informado' ? '' : defeitoData.observacao || '';
      const novoCodigoDefeito = selectedDefeito?.codigo || codDefeitoAnterior;
      const novaObs = (editObsValue != null ? String(editObsValue) : '').trim();
      const changedDefeito = !!(novoCodigoDefeito && novoCodigoDefeito !== codDefeitoAnterior);
      const changedObs = novaObs !== currentObs;

      if (isDefeitoEditing && changedDefeito && !isDefeitoObsConfirmOpen) {
        setEditObsValue(currentObs);
        setIsDefeitoObsConfirmOpen(true);
        return;
      }

      if (!changedDefeito && !changedObs) {
        handleCancelDefeitoObsEdit(defeitoData);
        return;
      }

      await persistDefeitoObs(defeitoData, setDefeitoData, {
        codigoDefeito: novoCodigoDefeito,
        observacao: isDefeitoObsConfirmOpen ? novaObs : isObsEditing ? novaObs : currentObs,
        codDefeitoAnterior,
      });
    },
    [
      order?.id,
      isSavingDefeitoObs,
      selectedDefeito,
      editObsValue,
      isDefeitoEditing,
      isObsEditing,
      isDefeitoObsConfirmOpen,
      handleCancelDefeitoObsEdit,
      persistDefeitoObs,
    ]
  );

  const handleConfirmDefeitoObsChange = React.useCallback(
    async (defeitoData, setDefeitoData) => {
      if (!defeitoData || isSavingDefeitoObs || !order?.id || !isDefeitoObsConfirmOpen) return;
      const codDefeitoAnterior = defeitoData.codDefeito || null;
      const novoCodigoDefeito = selectedDefeito?.codigo || codDefeitoAnterior;
      if (!novoCodigoDefeito) return;
      const novaObs = (editObsValue != null ? String(editObsValue) : '').trim();
      await persistDefeitoObs(defeitoData, setDefeitoData, {
        codigoDefeito: novoCodigoDefeito,
        observacao: novaObs,
        codDefeitoAnterior,
      });
    },
    [
      order?.id,
      isSavingDefeitoObs,
      isDefeitoObsConfirmOpen,
      selectedDefeito,
      editObsValue,
      persistDefeitoObs,
    ]
  );

  return {
    motivoDisplay,
    setMotivoDisplay,
    isMotivoEditing,
    motivoEditValue,
    setMotivoEditValue,
    isSavingMotivo,
    motivoSaveFeedback,
    handleStartMotivoEdit,
    handleCancelMotivoEdit,
    handleSaveMotivo,
    MOTIVO_OS_MAX,
    isDefeitoEditing,
    isObsEditing,
    defeitoSearchQuery,
    defeitoSearchResults,
    isDefeitoSearchLoading,
    defeitoSearchError,
    selectedDefeito,
    editObsValue,
    setEditObsValue,
    isSavingDefeitoObs,
    defeitoSaveFeedback,
    handleStartDefeitoEdit,
    handleStartObsEdit,
    handleDefeitoSearchChange,
    handleSelectDefeitoResult,
    handleClearSelectedDefeito,
    handleCancelDefeitoObsEdit,
    handleSaveDefeitoObs,
    isDefeitoObsConfirmOpen,
    handleCancelDefeitoObsConfirm,
    handleConfirmDefeitoObsChange,
    isObsGeralEditing,
    editObsGeralValue,
    setEditObsGeralValue,
    isSavingObsGeral,
    obsGeralSaveFeedback,
    handleStartObsGeralEdit,
    handleCancelObsGeralEdit,
    handleSaveObsGeral,
  };
}
